import type { BrowserWindow } from '@wix/thunderbolt-symbols'
import { isExperimentOpen } from '@wix/thunderbolt-experiments'
import _ from 'lodash'

export type PmRpc = {
	api: {
		set(appId: string, api: any): void
		request<T>(appId: string, target: { target: Element | Window | Worker }): Promise<T>
	}
}

declare global {
	interface Window {
		define?: Function & { amd: boolean }
		pmrpc?: PmRpc
	}
}

type ScriptType = 'module'
interface ScriptAttributes {
	type?: ScriptType
	'data-global-export-name'?: string
}

async function _loadScriptTag(src: string, attributes: ScriptAttributes = {}): Promise<unknown> {
	return new Promise((resolve, reject) => {
		if (typeof document === 'undefined') {
			reject(
				`document is not defined when trying to load script tag: ${src}. Make sure this script is not loaded on SSR`
			)
		}
		const script = document.createElement('script')
		script.src = src
		Object.entries(attributes).forEach(([name, value]) => {
			if (value !== undefined) {
				script.setAttribute(name, value)
			}
		})
		script.onerror = reject
		script.onload = resolve
		document.head.appendChild(script)
	})
}

export async function loadScriptTag(src: string): Promise<unknown> {
	return _loadScriptTag(src)
}

export async function loadModuleScriptTag(src: string): Promise<unknown> {
	return _loadScriptTag(src, { type: 'module' })
}

export function loadScriptWithRequireJS<T>(src: string): Promise<T> {
	return new Promise((resolve, reject) => __non_webpack_require__([src], resolve, reject))
}

export async function loadScriptWithGlobalExport<T>(src: string, globalName: string): Promise<T> {
	if (window.define?.amd) {
		throw new Error(`Cannot load ${src} as global export ${globalName}: an AMD loader is present`)
	}

	await _loadScriptTag(src, { 'data-global-export-name': globalName })
	const module = (window as unknown as Record<string, unknown>)[globalName]
	if (module === undefined) {
		throw new Error(`Script ${src} did not expose ${globalName}`)
	}

	return module as T
}

export const scriptUrls = (moduleRepoUrl: string) => ({
	PM_RPC: `${moduleRepoUrl}/pm-rpc@3.0.3/build/pm-rpc.min.js`,
	REQUIRE_JS: `${moduleRepoUrl}/requirejs-bolt@2.3.6/requirejs.min.js`,
})

export const loadPmRpc = async (moduleRepoUrl: string) => {
	if (window.pmrpc) {
		return window.pmrpc
	}
	if (window.define?.amd) {
		return loadScriptWithRequireJS<PmRpc>(scriptUrls(moduleRepoUrl).PM_RPC) as Promise<PmRpc>
	}
	await loadScriptTag(scriptUrls(moduleRepoUrl).PM_RPC)
	return window.pmrpc!
}

/**
 * RequireJS resolves an anonymous `define()` by attributing it to whichever script load completes
 * next: the definition is parked in a global queue, and `completeLoad()` binds the first unnamed
 * entry it finds to the module whose script just loaded. A script that requirejs did not inject can
 * therefore steal the identity of an unrelated module, and that module's real factory is discarded
 * (requirejs/require.js#completeLoad). The result is a resolved module that holds a completely
 * unrelated export, which is why it presents as a flaky "widget did not return a react component".
 *
 * Webpack chunks hit this: webpack leaves the `define` identifier untouched inside strict ESM
 * modules (HarmonyDetectionParserPlugin skips `define` for harmony modules, and `.mjs` is always
 * harmony), so a UMD header that was inlined into a pre-bundled `.mjs` dependency is compiled into
 * a call to *our* global `define` instead of into webpack's own module system.
 *
 * So anonymous defines are resolved here rather than by load order: requirejs tags every script it
 * injects with `data-requiremodule` (the module id it is waiting for), and `document.currentScript`
 * is that script while it evaluates - which names the module deterministically. An anonymous define
 * from anywhere else is not ours to resolve, and is ignored rather than left to poison the queue.
 */
const guardAnonymousDefine = (window: NonNullable<BrowserWindow>) => {
	const originalDefine = window.define
	if (!originalDefine) {
		return
	}
	const guardedDefine = function (this: unknown, ...args: Array<any>) {
		if (typeof args[0] === 'string') {
			return originalDefine.apply(this, args)
		}
		const moduleName = window.document.currentScript?.getAttribute('data-requiremodule')
		if (!moduleName) {
			return
		}
		return originalDefine.apply(this, [moduleName, ...args])
	}
	guardedDefine.amd = originalDefine.amd
	window.define = guardedDefine
}

export const loadRequireJS = _.once(
	async (window: NonNullable<BrowserWindow>, moduleRepoUrl = 'https://parastorage.cdn.inventica.ru/unpkg') => {
		// since react umd bundles do not define named modules, we must load react before loading requirejs.
		// further details here: https://requirejs.org/docs/errors.html#mismatch
		// requirejs will be hopefully removed once ooi comps will be consumed as comp libraries.
		await window.reactAndReactDOMLoaded
		await loadScriptTag(scriptUrls(moduleRepoUrl).REQUIRE_JS)
		// window.viewerModel is used directly since loadRequireJS is called from places that have no
		// access to the experiments object, and _.once would let the first caller decide for the page.
		// A missing viewerModel keeps the guard off, which is the pre-guard behaviour.
		if (isExperimentOpen(window.viewerModel?.experiments, 'specs.thunderbolt.guardAnonymousRequireJsDefine')) {
			guardAnonymousDefine(window)
		}
		window.define!('lodash', [], () => _)
		window.define!('_', [], () => _)
		window.define!('reactDOM', [], () => window.ReactDOM)
		window.define!('react', [], () => window.React)
		window.define!('imageClientSDK', [], () => window.__imageClientApi__.sdk)
		window.define!('imageClientApi', [], () => window.__imageClientApi__)
	}
)
